import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * Legal & Pricing Tab Component
 * Handles transaction type, ownership, loan details, and maintenance
 */
const LegalPricingTab = ({ 
  formData, 
  onInputChange, 
  onCheckboxChange 
}) => {
  const isRentOrLease = ['rent', 'lease'].includes(formData.purpose);

  return (
    <div className="space-y-6">
      {/* Transaction & Ownership Type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="transaction_type">Transaction Type <span className="text-red-500">*</span></Label>
          <select
            id="transaction_type"
            name="transaction_type"
            value={formData.transaction_type || ''}
            onChange={onInputChange}
            className="mt-1 h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-[#1e3a5f] focus:ring-1 focus:ring-[#1e3a5f]"
            data-testid="transaction-type-select"
            required
          >
            <option value="">Select Type</option>
            <option value="new_property">New Property</option>
            <option value="resale">Resale</option>
          </select>
        </div>

        <div>
          <Label htmlFor="ownership_type">Ownership Type <span className="text-red-500">*</span></Label>
          <select
            id="ownership_type"
            name="ownership_type"
            value={formData.ownership_type || ''}
            onChange={onInputChange}
            className="mt-1 h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-[#1e3a5f] focus:ring-1 focus:ring-[#1e3a5f]"
            data-testid="ownership-type-select"
            required
          >
            <option value="">Select Type</option>
            <option value="freehold">Freehold</option>
            <option value="leasehold">Leasehold</option>
            <option value="co_operative">Co-operative Society</option>
            <option value="power_of_attorney">Power of Attorney</option>
            <option value="joint_ownership">Joint Ownership</option>
            <option value="partnership_firm">Partnership Firm</option>
            <option value="company_owned">Company Owned</option>
            <option value="trust_owned">Trust Owned</option>
            <option value="government_lease">Government Lease</option>
          </select>
        </div>
      </div>

      {/* Existing Loan Information - Only for Sale/Resale/Purchase - NOT for Rent/Lease */}
      {!isRentOrLease && (
        <div className="bg-[#fffdf5] border border-[#d4af37]/30 rounded-lg p-4">
          <h4 className="text-lg font-semibold text-[#1e3a5f] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Existing Loan Details *
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="existing_loan_amount">Existing Loan Amount (Rs) *</Label>
              <Input
                id="existing_loan_amount"
                name="existing_loan_amount"
                type="number"
                value={formData.existing_loan_amount || ''}
                onChange={onInputChange}
                className="mt-1 h-11 rounded-lg"
                placeholder="Enter 0 if no loan"
                required
                data-testid="existing-loan-amount-input"
              />
              <p className="text-xs text-slate-500 mt-1">Enter 0 if there is no existing loan on property</p>
            </div>

            <div>
              <Label htmlFor="financial_institute_name">Financial Institute Name *</Label>
              <Input
                id="financial_institute_name"
                name="financial_institute_name"
                value={formData.financial_institute_name || ''}
                onChange={onInputChange}
                className="mt-1 h-11 rounded-lg"
                placeholder="HDFC Bank, SBI, ICICI, None"
                required
                data-testid="financial-institute-input"
              />
              <p className="text-xs text-slate-500 mt-1">Enter 'None' if no loan exists</p>
            </div>
          </div>
        </div>
      )}

      {/* RERA Registration */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="rera_registered"
            name="rera_registered"
            checked={formData.rera_registered || false}
            onCheckedChange={(checked) => onCheckboxChange('rera_registered', checked)}
            data-testid="rera-registered-checkbox"
          />
          <Label htmlFor="rera_registered" className="cursor-pointer">RERA Registered</Label>
        </div>

        {formData.rera_registered && (
          <div>
            <Label htmlFor="rera_id">RERA ID</Label>
            <Input
              id="rera_id"
              name="rera_id"
              value={formData.rera_id || ''}
              onChange={onInputChange}
              className="mt-1 h-11 rounded-lg"
              placeholder="Enter RERA registration number"
              data-testid="rera-id-input"
            />
          </div>
        )}
      </div>

      {/* Legal Charges and Booking */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="legal_charges">Legal Charges (Rs)</Label>
          <Input
            id="legal_charges"
            name="legal_charges"
            type="number"
            value={formData.legal_charges || ''}
            onChange={onInputChange}
            className="mt-1 h-11 rounded-lg"
            placeholder="50000"
            data-testid="legal-charges-input"
          />
          <p className="text-xs text-slate-500 mt-1">Legal and documentation charges</p>
        </div>

        {/* Booking Amount - Only for Sale/Resale/Purchase */}
        {!isRentOrLease && (
          <div>
            <Label htmlFor="booking_amount">Booking Amount (Rs)</Label>
            <Input
              id="booking_amount"
              name="booking_amount"
              type="number"
              value={formData.booking_amount || ''}
              onChange={onInputChange}
              className="mt-1 h-11 rounded-lg"
              placeholder="100000"
              data-testid="booking-amount-input"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default LegalPricingTab;
